import type { ReactNode } from "react";
import {
  IconArrowBackUp,
  IconBan,
  IconEye,
  IconHelpCircle,
  IconLayoutGrid,
  IconPhoto,
  IconPointer,
  IconRefresh,
  IconSquareDashed,
  IconSun,
  IconTrash,
  IconUser,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import {
  COVERAGE_BETA_HOTKEYS,
  hotkeyHint,
} from "@/hooks/useCoverageBetaHotkeys";
import type { TrainingStep } from "./CoverageBetaTrainingOverlay";

/** localStorage key for "has this labeler seen the Beta tutorial" (see
 * tutorial-storage.ts). Distinct from classic Coverage's
 * "coverage-training-dismissed" — the UIs differ enough that dismissing one
 * shouldn't hide the other. The "-v1" suffix is the lever to re-show the
 * tour after a material copy/UI rewrite. */
export const COVERAGE_BETA_TRAINING_STORAGE_KEY = "coverage-beta-tutorial-v1";

/** Inline colored icon glyph for step copy — mirrors the `I` helper in
 * classic Coverage's CoverageTrainingOverlay.tsx. */
const InlineIcon = ({
  children,
  color,
}: {
  children: ReactNode;
  color?: string;
}) => (
  <span style={{ display: "inline-flex", verticalAlign: "middle", color }}>
    {children}
  </span>
);

/** A hotkey hint, styled the same as the `<kbd>` used throughout the
 * toolbar/right-panel (QueryImageControlIcons.tsx, CoverageBetaRightPanel.tsx)
 * so the tutorial's hints look like the ones a labeler will actually see. */
const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd className="text-[10px] font-mono text-gray-500 border rounded px-1">
    {children}
  </kbd>
);

/**
 * The Coverage Beta onboarding tutorial's steps. `anchors` values must be
 * real `data-cb-tour` attributes (the steps test checks this against the
 * actual component files).
 *
 * The four removal reasons named in `delete-when` below (isn't really an
 * empty area / product facings / blue tag / main aisle) mirror
 * `AREA_QA_REASONS` (coverage-beta-qa/CoverageBetaQaPanel.tsx) and
 * `EMPTY_AREA_OPTIONS` (config/data.ts) — keep the three in sync; the steps
 * test asserts this description mentions each reason.
 */
export const COVERAGE_BETA_TRAINING_STEPS: TrainingStep[] = [
  {
    id: "welcome",
    title: "Welcome to Out of Stock",
    description: (
      <>
        Your job is to find empty shelf areas and figure out which product is
        missing from each one.
        <br />
        <br />
        You&apos;ll review auto-detected boxes, draw new ones where the detector
        missed something, pick the right product from a shelf photo or the
        planogram, and submit once every box is reviewed.
        <br />
        <br />
        Use <Kbd>←</Kbd> / <Kbd>→</Kbd> to move through this walkthrough, or{" "}
        <Kbd>Esc</Kbd> to exit it any time.
      </>
    ),
    anchors: [],
    placement: "center",
  },
  {
    id: "sidebar",
    title: "Your box list",
    description: (
      <>
        Every box on the shelf photo lands in one of four buckets here:
        <br />• Out of Stock Confirmed — a product has been picked for it
        <br />• Empty Area — reviewed, but no product matched
        <br />• Unblurred Persons — people that need flagging
        <br />• Deleted — boxes you removed
        <br />
        <br />
        Click a row to select it and zoom to it on the shelf photo. The green
        check on a row means it&apos;s reviewed — Submit needs every non-deleted
        box checked.
      </>
    ),
    anchors: ["sidebar"],
    placement: "right",
  },
  {
    id: "toolbar-classes",
    title: "Drawing classes",
    description: (
      <>
        Pick a class before drawing on the shelf photo:
        <br />
        <InlineIcon color="#7950f2">
          <IconSquareDashed size={14} />
        </InlineIcon>{" "}
        Draw Empty Area (<Kbd>{hotkeyHint("draw_box")}</Kbd>) — an empty facing
        the detector missed.
        <br />
        <InlineIcon color="#fd7e14">
          <IconUser size={14} />
        </InlineIcon>{" "}
        Draw Person (<Kbd>{hotkeyHint("draw_person")}</Kbd>) — anyone whose face
        or body wasn&apos;t blurred.
      </>
    ),
    anchors: ["toolbar-classes", "toolbar-tools", "sidebar"],
    placement: "right",
  },
  {
    id: "toolbar-tools",
    title: "Viewing tools",
    description: (
      <>
        <InlineIcon>
          <IconPointer size={14} />
        </InlineIcon>{" "}
        Cursor (<Kbd>{hotkeyHint("cursor")}</Kbd>) — pan around without drawing.
        <br />
        <InlineIcon>
          <IconZoomIn size={14} />
        </InlineIcon>{" "}
        /{" "}
        <InlineIcon>
          <IconZoomOut size={14} />
        </InlineIcon>{" "}
        Zoom (<Kbd>{hotkeyHint("zoom_in")}</Kbd>/
        <Kbd>{hotkeyHint("zoom_out")}</Kbd>),{" "}
        <InlineIcon>
          <IconRefresh size={14} />
        </InlineIcon>{" "}
        Reset view (<Kbd>{hotkeyHint("reset_view")}</Kbd>).
        <br />
        <InlineIcon>
          <IconEye size={14} />
        </InlineIcon>{" "}
        Hide/show boxes (<Kbd>{hotkeyHint("toggle_boxes")}</Kbd>) — see the
        shelf underneath.
        <br />
        <InlineIcon>
          <IconSun size={14} />
        </InlineIcon>{" "}
        Brightness (<Kbd>{hotkeyHint("brightness_up")}</Kbd>/
        <Kbd>{hotkeyHint("brightness_down")}</Kbd>) — brighten a dark shelf
        before deciding it&apos;s empty.
      </>
    ),
    anchors: ["toolbar-tools", "sidebar"],
    placement: "right",
  },
  {
    id: "draw-when",
    title: "When to draw a box",
    description: (
      <>
        Draw an <b>Empty Area</b> box for a genuinely empty facing the detector
        missed — click and drag on the shelf photo.
        <br />
        <br />
        Draw a <b>Person</b> box over anyone whose face or body wasn&apos;t
        blurred, so it gets flagged.
        <br />
        <br />
        Zoom in first for accuracy — boxes snap to wherever you draw them.
      </>
    ),
    anchors: ["query-pane"],
    placement: "right",
  },
  {
    id: "delete-when",
    title: "When to remove a box",
    description: (
      <>
        The{" "}
        <InlineIcon>
          <IconTrash size={14} />
        </InlineIcon>{" "}
        icon (or <Kbd>{hotkeyHint("delete_area")}</Kbd>) removes a box. Remove
        it when it shouldn&apos;t be in this task at all:
        <br />• it isn&apos;t really an empty area
        <br />• there are still product facings in it
        <br />• a blue tag is present
        <br />• it isn&apos;t in the main aisle
        <br />
        <br />
        Removed boxes move to Deleted and can be restored with the{" "}
        <InlineIcon>
          <IconArrowBackUp size={14} />
        </InlineIcon>{" "}
        undo arrow.
      </>
    ),
    anchors: ["summary-list", "sidebar"],
    placement: "right",
  },
  {
    id: "right-tabs",
    title: "Two ways to identify the product",
    description: (
      <>
        <InlineIcon>
          <IconPhoto size={14} />
        </InlineIcon>{" "}
        Shelf images and{" "}
        <InlineIcon>
          <IconLayoutGrid size={14} />
        </InlineIcon>{" "}
        Planogram — <Kbd>{hotkeyHint("toggle_view")}</Kbd> toggles between them.
      </>
    ),
    anchors: ["right-view-toggle", "right-panel"],
    placement: "left",
  },
  {
    id: "shelf-images",
    title: "Shelf images",
    description: (
      <>
        Earlier photos of this same shelf, from when it was stocked — the clock
        badge shows how old each one is. Thumbnails top-left,{" "}
        <Kbd>{hotkeyHint("prev_ref")}</Kbd>/<Kbd>{hotkeyHint("next_ref")}</Kbd>{" "}
        to step through them.
        <br />
        <br />
        Click the product box sitting where the empty area is now to attach it.
      </>
    ),
    anchors: ["shelf-images-view", "right-panel-body", "right-panel"],
    placement: "left",
  },
  {
    id: "confirm-step",
    title: "Confirming the product",
    description: (
      <>
        Clicking a product on a shelf image opens a confirm panel below with up
        to five candidates, plus a red &quot;No match&quot; card — pick the
        right one. Other areas stay locked until you do.
        <div className="grid grid-cols-4 gap-1.5 mt-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-10 rounded border border-gray-200 bg-gray-100"
            />
          ))}
          <div className="h-10 rounded border-2 border-red-300 bg-red-50 flex items-center justify-center">
            <IconBan size={14} className="text-red-400" />
          </div>
        </div>
      </>
    ),
    anchors: [],
    placement: "center",
  },
  {
    id: "planogram",
    title: "Planogram",
    description: (
      <>
        The store&apos;s layout diagram — what should be on that shelf. Use it
        when the shelf photos are too old, too dark, or don&apos;t cover the
        spot.
        <br />
        <br />
        Click the product tile to assign it. Tabs at the top when a task spans
        several planograms.
      </>
    ),
    anchors: ["planogram-view", "right-panel-body", "right-panel"],
    placement: "left",
  },
  {
    id: "no-match",
    title: '"No match" vs. removing a box',
    description: (
      <>
        Your picks list here — each row can be confirmed, changed, or removed.
        <br />
        <br />
        <b>No match</b> (<Kbd>{hotkeyHint("no_match")}</Kbd>) means: this really
        is an empty area, but no product can be matched to it — the box stays in
        the task and counts as reviewed.
        <br />
        <br />
        That&apos;s different from <b>removing</b> a box, which says the box
        shouldn&apos;t be here at all.
      </>
    ),
    anchors: ["right-footer", "right-panel"],
    placement: "left",
  },
  {
    id: "shortcuts",
    title: "All shortcuts",
    description: (
      <div className="max-h-56 overflow-y-auto -mr-2 pr-2">
        {COVERAGE_BETA_HOTKEYS.map((b) => (
          <div
            key={b.action}
            className="flex items-center justify-between gap-3 py-0.5"
          >
            <span>{b.description}</span>
            <Kbd>{hotkeyHint(b.action)}</Kbd>
          </div>
        ))}
      </div>
    ),
    anchors: [],
    placement: "center",
  },
  {
    id: "submit",
    title: "Finishing up",
    description: (
      <>
        Submit unlocks once every box you kept is reviewed — a confirmed
        product, or No match. Skip if the image itself is unusable.
        <br />
        <br />
        Reopen this walkthrough any time from the{" "}
        <InlineIcon>
          <IconHelpCircle size={14} />
        </InlineIcon>{" "}
        icon next to &quot;Out of Stock&quot;.
      </>
    ),
    anchors: ["top-bar-actions"],
    placement: "bottom",
  },
];
